<template>
  <wz-modal :value="value" :title="title" size="md" @input="$emit('input', $event)">
    <div v-if="definition">
      <wz-alert v-if="definition.warning" variant="warning" icon="ri-error-warning-line" class="mb-3">
        {{ definition.warning }}
      </wz-alert>

      <p class="mb-3 text-muted text-sm">
        <span v-if="definition.legalBasis">Base legal: {{ definition.legalBasis }}.</span>
        <span v-if="isEdit">
          Las fechas no se editan. Para cambiar el valor desde una fecha, cree una nueva vigencia.
        </span>
        <span v-else>
          La vigencia anterior se cerrará automáticamente el día previo a la fecha de inicio.
        </span>
      </p>

      <div v-if="!isEdit && definition.scope === 'REGIME'" class="mb-3">
        <label class="font-medium">Régimen laboral <span class="text-danger">*</span></label>
        <wz-select v-model="form.laborRegime" :items="regimeOptions" defaultOption="Seleccionar régimen"
          :disabled="!!presetRegime" />
      </div>

      <div v-if="!isEdit && definition.scope === 'COMPANY'" class="mb-3">
        <label class="font-medium">Empresa</label>
        <div>{{ companyName || '—' }}</div>
      </div>

      <div v-if="!isEdit" class="mb-3">
        <label class="font-medium">Vigente desde <span class="text-danger">*</span></label>
        <wz-datepicker v-model="form.validFrom" />
      </div>
      <div v-else class="mb-3">
        <label class="font-medium">Vigencia</label>
        <div>{{ formatDate(row.validFrom) }} — {{ row.validTo ? formatDate(row.validTo) : 'vigente' }}</div>
      </div>

      <div class="mb-3">
        <wz-input :label="valueLabel" required type="number" v-model.number="form.displayValue" step="any"
          :suffix="isPercent ? '%' : ''" />
        <small v-if="definition.unit === 'FLAG'" class="text-muted">1 = aplica, 0 = no aplica</small>
      </div>

      <div class="mb-3">
        <wz-input label="Base legal del valor" v-model="form.legalBasis" maxlength="300"
          placeholder="Ej: D.S. 015-2026-TR" />
      </div>

      <div class="mb-3">
        <label class="font-medium">Notas</label>
        <wz-textarea v-model="form.notes" rows="3" maxlength="500" />
      </div>
    </div>

    <template #footer>
      <wz-button variant="outline-secondary" @click="$emit('input', false)">Cancelar</wz-button>
      <wz-button variant="primary" :disabled="saving || !canSave" @click="save">
        {{ isEdit ? 'Guardar cambios' : 'Crear vigencia' }}
      </wz-button>
    </template>
  </wz-modal>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { LegalParameterService } from '@/core/services'

export const REGIME_OPTIONS = [
  { key: 'GENERAL', label: 'General' },
  { key: 'MYPE_PEQUENA', label: 'MYPE pequeña' },
  { key: 'MYPE_MICRO', label: 'MYPE micro' },
  { key: 'AGRARIO', label: 'Agrario' },
  { key: 'CONSTRUCCION', label: 'Construcción' },
  { key: 'HOGAR', label: 'Hogar' },
]

export default {
  name: 'LegalParameterVigenciaModal',
  props: {
    value: { type: Boolean, default: false },
    definition: { type: Object, default: null },
    // Fila a editar (modo edición). Null → nueva vigencia.
    row: { type: Object, default: null },
    presetRegime: { type: String, default: null },
    companyId: { type: [Number, String], default: null },
    companyName: { type: String, default: null },
  },
  data() {
    return {
      saving: false,
      regimeOptions: REGIME_OPTIONS,
      form: { laborRegime: null, validFrom: null, displayValue: null, legalBasis: '', notes: '' },
    }
  },
  computed: {
    isEdit() { return !!this.row },
    isPercent() { return this.definition && this.definition.unit === 'RATE' },
    title() {
      if (!this.definition) return ''
      return (this.isEdit ? 'Editar vigencia — ' : 'Nueva vigencia — ') + this.definition.label
    },
    valueLabel() { return this.isPercent ? 'Valor (%)' : 'Valor' },
    canSave() {
      if (this.form.displayValue === null || this.form.displayValue === '' || isNaN(this.form.displayValue)) return false
      if (this.isEdit) return true
      if (!this.form.validFrom) return false
      if (this.definition.scope === 'REGIME' && !this.form.laborRegime) return false
      if (this.definition.scope === 'COMPANY' && !this.companyId) return false
      return true
    },
  },
  watch: {
    value(open) { if (open) this.reset() },
  },
  methods: {
    reset() {
      const r = this.row
      this.form = {
        laborRegime: this.presetRegime || null,
        validFrom: null,
        displayValue: r ? this.toDisplay(r.value) : null,
        legalBasis: r ? (r.legalBasis || '') : '',
        notes: r ? (r.notes || '') : '',
      }
    },
    // Las tasas se guardan como fracción (0.10) y se editan como porcentaje (10).
    toDisplay(v) { return v == null ? null : (this.isPercent ? parseFloat((Number(v) * 100).toFixed(6)) : Number(v)) },
    toStored(v) { return this.isPercent ? Number((Number(v) / 100).toFixed(6)) : Number(v) },
    formatDate(iso) {
      if (!iso) return ''
      const [y, m, d] = String(iso).split('-')
      return `${d}/${m}/${y}`
    },
    async save() {
      if (!this.canSave) return
      this.saving = true
      try {
        const value = this.toStored(this.form.displayValue)
        if (this.isEdit) {
          await LegalParameterService.updateVigencia(this.row.id, {
            value, legalBasis: this.form.legalBasis || null, notes: this.form.notes || null,
          })
          Alert.success('Vigencia actualizada')
        } else {
          await LegalParameterService.createVigencia({
            code: this.definition.code,
            laborRegime: this.definition.scope === 'REGIME' ? this.form.laborRegime : null,
            companyId: this.definition.scope === 'COMPANY' ? Number(this.companyId) : null,
            validFrom: this.form.validFrom,
            value,
            legalBasis: this.form.legalBasis || null,
            notes: this.form.notes || null,
          })
          Alert.success('Vigencia creada')
        }
        this.$emit('saved')
        this.$emit('input', false)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar la vigencia')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
